import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { TranslatePipe } from '@ngx-translate/core';
import { ICostReviewRequestCCRecipient } from '../../../../../models/common/ICostReviewRequestCCRecipient';
import { SolPreferencesService } from '../../../../../services/preferences/sol-preferences.service';
import { MatDialog } from '@angular/material/dialog';
import { CONSTANTS } from '../../../../../Constants/Constants';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { CostReviewAddCcRecipient } from '../../../../dialogs/common/cost-review-add-cc-recipient/cost-review-add-cc-recipient';

@Component({
  selector: 'app-sol-preferences-cost-review-cc',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    TranslatePipe
  ],
  templateUrl: './sol-preferences-cost-review-cc.component.html',
  styleUrls: ['./sol-preferences-cost-review-cc.component.scss']
})
export class SolPreferencesCostReviewCcComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly compareByEmail = (a: ICostReviewRequestCCRecipient, b: ICostReviewRequestCCRecipient): boolean =>
    a.email === b.email;

  // --- Top table: Email CC Recipients ---
  ccRecipientsColumns: string[] = ['email'];
  ccRecipientsDataSource = new MatTableDataSource<ICostReviewRequestCCRecipient>();
  ccRecipientsSelection = new SelectionModel<ICostReviewRequestCCRecipient>(false, [], true, this.compareByEmail);

  // --- Bottom table: Available Email CC Recipients ---
  availableColumns: string[] = ['email', 'remove'];
  availableDataSource = new MatTableDataSource<ICostReviewRequestCCRecipient>();
  availableSelection = new SelectionModel<ICostReviewRequestCCRecipient>(false, [], true, this.compareByEmail);

  constructor(
    private readonly preferencesService: SolPreferencesService,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.initializeForm();
  }

  // --- Initialization (to be implemented) ---

  private initializeForm(): void {
    const userPreferences = this.preferencesService.getUserPreferences();
    const costReviewerCcPrefs = userPreferences?.costReviewerPreferences;

    this.ccRecipientsDataSource.data = (costReviewerCcPrefs?.crEmailCCUsers ?? []).map(email => ({ email }));
    this.availableDataSource.data = (costReviewerCcPrefs?.crAvailableCCUsers ?? []).map(email => ({ email }));
  }

  // ---- Row click handlers ----

  onCcRecipientRowClick(row: ICostReviewRequestCCRecipient): void {
    this.ccRecipientsSelection.toggle(row);
    this.availableSelection.clear();
  }

  onAvailableRowClick(row: ICostReviewRequestCCRecipient): void {
    this.availableSelection.toggle(row);
    this.ccRecipientsSelection.clear();
  }

  // ---- Button states ----

  get isAddEnabled(): boolean {
    return !(this.availableSelection.isEmpty() || this.ccRecipientsDataSource.data.length === CONSTANTS.THREE); 
  }

  get isRemoveEnabled(): boolean {
    return !this.ccRecipientsSelection.isEmpty();
  }

  get isAddCcRecipientEnabled(): boolean {
    return (this.availableDataSource.data.length !== CONSTANTS.TWENTY);
  }

  // ---- Actions ----

  addToCcRecipients(): void {
    if (this.availableSelection.isEmpty()) {
      return;
    }

    const selected = this.availableSelection.selected[0];

    this.ccRecipientsDataSource.data = [...this.ccRecipientsDataSource.data, selected];
    this.availableDataSource.data = this.availableDataSource.data.filter(r => r.email !== selected.email);
    this.availableSelection.clear();
    this.syncToService();
  }

  removeFromCcRecipients(): void {
    if (this.ccRecipientsSelection.isEmpty()) {
      return;
    }

    const selected = this.ccRecipientsSelection.selected[0];

    this.availableDataSource.data = [...this.availableDataSource.data, selected];
    this.ccRecipientsDataSource.data = this.ccRecipientsDataSource.data.filter(r => r.email !== selected.email);
    this.ccRecipientsSelection.clear();
    this.syncToService();
  }

  deleteAvailableRecipient(row: ICostReviewRequestCCRecipient, $event: Event): void {
    $event.stopPropagation();
    this.availableDataSource.data = this.availableDataSource.data.filter(r => r !== row);

    if (this.availableSelection.isSelected(row)) {
      this.availableSelection.clear();
    }
    this.syncToService();
  }

  addCcRecipient(): void {
    const dialogRef = this.dialog.open(CostReviewAddCcRecipient, {
      width: '600px',
      height: '150px',
      disableClose: true,
      data: {
        isCostReviewRequest: false
      }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((email: string) => {

      if (email) {
        this.availableDataSource.data = [...this.availableDataSource.data, { email }];
        this.syncToService();
      }
    })
  }

  // Track-by helper
  trackByEmail(_index: number, row: ICostReviewRequestCCRecipient): string {
    return row.email;
  }

  /** Push current table state back into the shared service. */
  private syncToService(): void {
    const mapToDetails = (rows: ICostReviewRequestCCRecipient[]): string[] => rows.map(r => r.email);

    this.preferencesService.silentSetPreferences(prefs => {
      prefs.costReviewerPreferences = {
        crReviewers: prefs.costReviewerPreferences?.crReviewers, // Not managed in this component
        crAvailableReviewers: prefs.costReviewerPreferences?.crAvailableReviewers, // Not managed in this component
        crAvailableCCUsers: mapToDetails(this.availableDataSource.data),
        crEmailCCUsers: mapToDetails(this.ccRecipientsDataSource.data),
      };
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}